import useMediaQuery from "@mui/material/useMediaQuery";
import { useContext, useEffect } from "react";
import { Upload } from "lucide-react";
import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
import {
  LossRunsReadContext,
  useLossRuns,
} from "@/components/submissions/lossRuns/lossRunsContext";
import type { SubmissionDetail } from "@/lib/submissions/brokerSubmissions";
import {
  lossRunsBadge,
  lossRunsButtonName,
  needsLossRuns,
} from "@/lib/submissions/lossRuns";

// The header's loss runs (BRK-S361; Nav, 2026-10-15: "display a button on the
// header which can used by a broker to upload the loss runs … display an
// indicator in the header that loss runs is required, animate it so that it
// catches the brokers' eyes"), on the submission's page and in its
// application's header. Both open the panel, and both show only once PMS
// says where the loss runs stand.

/** A pulsing dot: amber, the stepper's attention colour, with a ring growing
 *  out of it about every 1.8s while motion is welcome
 *  (docs/design-system.md § The attention pill). Decoration: the words beside
 *  it say what it means. */
export function AttentionDot() {
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)", {
    noSsr: true,
  });
  return (
    <span aria-hidden="true" className="relative flex h-2 w-2 shrink-0">
      {!reduceMotion && (
        <span
          data-ring
          className="absolute inset-0 rounded-full bg-amber-600 opacity-60 motion-safe:animate-attention-ring motion-reduce:hidden dark:bg-amber-300"
        />
      )}
      <span className="relative h-2 w-2 rounded-full bg-amber-700 dark:bg-amber-300" />
    </span>
  );
}

/** "Loss runs required", beside the status chip while the header asks for
 *  them (`needsLossRuns`): gone once one is on file or a reason is recorded,
 *  and never on a bound submission. A chip in shape, it's a button: it opens
 *  the panel. */
export function LossRunsPill() {
  const value = useLossRuns();
  if (!value || !needsLossRuns(value.lossRuns, value.locked)) return null;
  return (
    <button
      type="button"
      onClick={value.show}
      className={`inline-flex items-center gap-2 rounded-full border border-amber-600/60 bg-amber-50 py-0.5 pl-2 pr-2.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 dark:border-amber-300/50 dark:bg-amber-400/10 dark:text-amber-300 dark:hover:bg-amber-400/20 ${TOUCH_TARGET}`}
    >
      <AttentionDot />
      Loss runs required
    </button>
  );
}

/** Hands the page's provider the wizard's latest read of the submission, so
 *  a save that recorded a reason (BRK-S389) moves the pill and the button on
 *  at once. Draws nothing. */
export function LossRunsFrom({ submission }: { submission: SubmissionDetail }) {
  const adopt = useContext(LossRunsReadContext);
  useEffect(() => {
    adopt?.(submission);
  }, [adopt, submission]);
  return null;
}

/** The header's first action: an amber dot while they're asked for, their
 *  count once on file. Named by where they stand ("Loss runs, 2 on file"). */
export function LossRunsButton() {
  const value = useLossRuns();
  if (!value) return null;
  const badge = lossRunsBadge(value.lossRuns, value.locked);
  return (
    <button
      ref={value.buttonRef}
      type="button"
      onClick={value.show}
      aria-label={lossRunsButtonName(value.lossRuns, value.locked)}
      className={`${buttonClass({ variant: "secondary" })} relative`}
    >
      <Upload aria-hidden="true" className="h-4 w-4" />
      Loss runs
      {badge === "dot" ? (
        <span
          data-badge="dot"
          aria-hidden="true"
          className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-brk-panel bg-amber-600 dark:bg-amber-300"
        />
      ) : typeof badge === "number" ? (
        <span
          data-badge="count"
          aria-hidden="true"
          className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brk-accent/10 px-1.5 text-caption font-semibold tabular-nums text-brk-accent"
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}
